import { getLogger } from '@packages/core/logging';
import { CaseType, WorksheetForm, WorksheetCaseTypeStatus, WorksheetCaseGroup } from '@packages/models/api/worksheets';
import { worksheetsService } from '@web/services/singletons';
import { useCallback, useEffect, useState } from 'react';
import useTranslationData from '@web/administration/components/worksheet/hooks/useTranslationData';
import useWorksheetPermission from './use-worksheet-permission';
import { WorksheetAction } from '../types';

const logger = getLogger('admin');

interface State {
    nameErrorMessage: string;
    titleErrorMessage: string;
    worksheetGroupErrorMessage: string;
    caseTypeErrorMessage: string;
    caseTypes: CaseType[];
    caseGroups: WorksheetCaseGroup[];
}
interface Props {
    setWorkSheetInformation: (nextState: Partial<WorksheetForm>) => void;
    worksheetInfo: WorksheetForm;
    worksheetAction?: WorksheetAction;
    onInlineUpdateError?: (message: string) => void;
}

const useWorksheetForm = ({ setWorkSheetInformation, worksheetInfo, worksheetAction, onInlineUpdateError }: Props) => {
    const [state, _setState] = useState<State>({
        nameErrorMessage: '',
        titleErrorMessage: '',
        worksheetGroupErrorMessage: '',
        caseTypeErrorMessage: '',
        caseTypes: [],
        caseGroups: [],
    });
    const { validationMessageDataTranslate } = useTranslationData();
    const [isCaseTypeLoading, setIsCaseTypeLoading] = useState(false);
    const [isCaseGroupsLoading, setIsCaseGroupsLoading] = useState(false);
    const errors = {
        title: validationMessageDataTranslate.title,
        name: validationMessageDataTranslate.name,
        worksheetGroup: validationMessageDataTranslate.worksheetGroup,
        caseType: validationMessageDataTranslate.caseType,
    };
    const { isPilotUser } = useWorksheetPermission();
    const setState = useCallback((nextState: Partial<State>) => {
        _setState((prevState) => ({ ...prevState, ...nextState }));
    }, []);

    useEffect(() => {
        setIsCaseTypeLoading(true);
        fetchCaseType();
        fetchCaseGroups();
    }, [isPilotUser]);

    const fetchCaseType = useCallback(async () => {
        try {
            const caseTypeData = await worksheetsService.fetchCaseType({
                status: WorksheetCaseTypeStatus.Active,
            });
            if (!caseTypeData?.success) {
                setIsCaseTypeLoading(false);
                throw caseTypeData?.data;
            }
            setState({ caseTypes: caseTypeData.data.casetypes });
            // Removed automatic case type selection for new worksheets
            setIsCaseTypeLoading(false);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to fetch case type';
            logger.error('Failed to fetch case types', { error: e });
            throw e instanceof Error ? e : new Error(message);
        }
    }, [setState, setWorkSheetInformation, worksheetInfo?.caseType, worksheetAction]);

    const handleTitleChange = useCallback(
        (title: string) => {
            setWorkSheetInformation({ displayname: title });
            if (!title) {
                setState({ titleErrorMessage: errors.title });
                return;
            }
            setState({ titleErrorMessage: '' });
        },
        [errors.title, setState, setWorkSheetInformation]
    );

    const handleDescriptionChange = useCallback(
        (description: string) => {
            setWorkSheetInformation({ description: description || '' });
        },
        [setWorkSheetInformation]
    );

    const handleNameChange = useCallback(
        (name: string) => {
            setWorkSheetInformation({ worksheetGroupName: name });
            if (!name) {
                setState({ nameErrorMessage: errors.name });
                return;
            }
            setState({ nameErrorMessage: '' });
        },
        [errors.name, setState, setWorkSheetInformation]
    );

    const fetchCaseGroups = useCallback(async () => {
        setIsCaseGroupsLoading(true);
        try {
            const caseGroupsData = await worksheetsService.fetchWorksheetCaseGroups();
            if (caseGroupsData?.success) {
                // Sort groups alphabetically by groupName
                const sortedGroups = (caseGroupsData.data || []).sort((a, b) => a.groupName.localeCompare(b.groupName));
                setState({ caseGroups: sortedGroups });
            } else {
                // API call failed
                logger.error('Failed to fetch case groups', { error: caseGroupsData?.data });
                setState({ caseGroups: [] });
                onInlineUpdateError?.('Unable to load worksheet groups. Please refresh the page.');
            }
        } catch (error) {
            logger.error('Failed to fetch case groups', { error });
            setState({ caseGroups: [] });
            onInlineUpdateError?.('Unable to load worksheet groups. Please refresh the page.');
        } finally {
            setIsCaseGroupsLoading(false);
        }
    }, [setState, onInlineUpdateError]);

    const handleChangeCaseType = useCallback(
        (value: string) => {
            setWorkSheetInformation({ caseTypeId: value });
            const caseType = state.caseTypes.find((caseType) => caseType?.caseTypeId === value);
            if (caseType) {
                setWorkSheetInformation({ northstarId: caseType.northstarId, caseType: caseType.caseType });
            }
            // Validation logic
            if (!value) {
                setState({ caseTypeErrorMessage: errors.caseType });
                return;
            }
            setState({ caseTypeErrorMessage: '' });
        },
        [setWorkSheetInformation, state.caseTypes, errors.caseType, setState]
    );

    const handleChangeWorksheetGroup = useCallback(
        (groupId: string) => {
            const group = state.caseGroups.find((g) => g.worksheetCaseGroupId === groupId);
            if (group) {
                setWorkSheetInformation({ worksheetCaseGroupId: groupId, caseGroupName: group.groupName });
            } else {
                setWorkSheetInformation({ worksheetCaseGroupId: '', caseGroupName: '' });
            }
            // Validation logic
            if (!groupId) {
                setState({ worksheetGroupErrorMessage: errors.worksheetGroup });
                return;
            }
            setState({ worksheetGroupErrorMessage: '' });
        },
        [setWorkSheetInformation, state.caseGroups, errors.worksheetGroup, setState]
    );

    return {
        state,
        errors,
        setState,
        actions: {
            handleChangeCaseType,
            handleNameChange,
            handleTitleChange,
            handleDescriptionChange,
            handleChangeWorksheetGroup,
            isCaseTypeLoading,
            isCaseGroupsLoading,
        },
    };
};

export type UseWorksheetFormReturnType = ReturnType<typeof useWorksheetForm>;

export default useWorksheetForm;
